import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { whyChoose, whyImage } from '@/mocks/sections';
import onView from '@/hooks/onView';

gsap.registerPlugin(ScrollTrigger, useGSAP);

export default function WhyChooseSection() {
  const ref = useRef<HTMLElement>(null);
  useSectionReveal(ref);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(ref);
      const frame = q('[data-w-frame]')[0];
      const offs: Array<() => void> = [];
      const ftl = gsap.timeline({ paused: true })
        .fromTo(frame, { clipPath: 'inset(9% 7% 9% 7%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.05, ease: 'power3.inOut' })
        .fromTo(q('[data-w-frame] img'), { y: 20 }, { y: 0, duration: 1.2, ease: 'power3.out' }, 0);
      offs.push(onView(frame, () => ftl.restart(), () => ftl.pause(0), '0px 0px -20% 0px'));

      q('[data-w-item]').forEach((item: HTMLElement, i: number) => {
        const el = gsap.utils.selector(item);
        const itl = gsap.timeline({ paused: true, delay: (i % 2) * 0.1 });
        offs.push(onView(item, () => itl.restart(true), () => itl.pause(0), '0px 0px -10% 0px'));
        itl
          .fromTo(el('[data-w-line]'), { scaleX: 0 }, { scaleX: 1, duration: 0.9, ease: 'power3.inOut' })
          .fromTo(el('[data-w-title]'), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.2)
          .fromTo(el('[data-w-text]'), { y: 10, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.3);
      });
      return () => offs.forEach((off) => off());
    });
  }, { scope: ref });

  return (
    <section ref={ref} id="why" className="bg-background-50 px-4 md:px-10 pb-20 md:pb-32">
      <div className="max-w-7xl mx-auto">
        <div data-w-frame className="w-full h-[280px] md:h-[560px] rounded-none overflow-hidden">
          <img loading="lazy" decoding="async" src={whyImage} alt="Home with new windows at golden hour" title="Why homeowners choose Northlight windows" className="w-full h-full object-cover object-top scale-[1.04]" />
        </div>
        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <p data-reveal className="text-[11px] uppercase tracking-[0.24em] text-primary-600">Why choose us</p>
            <h2 data-reveal className="mt-4 font-heading font-light text-3xl md:text-5xl leading-[1.1] text-foreground-950">Care you can see in the finish.</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 sm:grid-cols-2 border-b border-background-300/70">
            {whyChoose.map((w) => (
              <div key={w.title} data-w-item className="relative py-6 sm:pr-8">
                <span data-w-line aria-hidden="true" className="absolute left-0 right-0 top-0 h-px bg-background-300/70 origin-left" />
                <h3 data-w-title className="font-heading text-lg md:text-xl text-primary-900">{w.title}</h3>
                <p data-w-text className="mt-2 text-sm text-foreground-600 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}